import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface UseEdgeDragOptions {
  clampToEdge?: 'top' | 'right' | 'bottom' | 'left'
  storageKey?: string
  elementSize: { width: number; height: number }
  isDraggingDisabled?: boolean
  isShadowDisabled?: boolean
  onNotchPositionChanged?: (position: {
    top: number
    left: number
    width: number
    height: number
  }) => void
}

export function useEdgeDrag({
  clampToEdge,
  storageKey,
  elementSize,
  isDraggingDisabled,
  isShadowDisabled,
  onNotchPositionChanged,
}: UseEdgeDragOptions) {
  const [relativePosition, setRelativePosition] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef<null | { startPos: number; initial: number }>(null)
  const animationFrameId = useRef<number | null>(null)
  const axis = clampToEdge === 'left' || clampToEdge === 'right' ? 'vertical' : 'horizontal'
  const [dimensions, setDimensions] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }))

  useEffect(() => {
    const update = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', update)
    update()
    return () => window.removeEventListener('resize', update)
  }, [])

  // load stored relative position if it exists
  useEffect(() => {
    if (!storageKey || isDraggingDisabled) return

    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const value = parseFloat(stored)
      if (!isNaN(value)) {
        setRelativePosition(value)
      }
    }
  }, [storageKey, isDraggingDisabled])

  const absolutePosition = useMemo(() => {
    if (isDraggingDisabled) {
      return isShadowDisabled ? 3 : 25
    }

    let result: number
    if (relativePosition == null) {
      // Calculate default position (50% centered)
      const base = axis === 'vertical' ? dimensions.height : dimensions.width
      const elSize = axis === 'vertical' ? elementSize.height : elementSize.width
      result = Math.round(base / 2 - elSize / 2)

      // Apply clamping if needed
      if (clampToEdge) {
        result = Math.max(0, Math.min(result, base - elSize))
      }
    } else {
      const base = axis === 'vertical' ? dimensions.height : dimensions.width
      const elSize = axis === 'vertical' ? elementSize.height : elementSize.width
      const raw = relativePosition * base
      result = clampToEdge ? Math.max(0, Math.min(raw, base - elSize)) : raw
    }

    // Always call onNotchPositionChanged when callback is provided
    if (onNotchPositionChanged) {
      const top =
        axis === 'vertical'
          ? result
          : clampToEdge === 'bottom'
            ? Math.max(0, dimensions.height - elementSize.height)
            : 0
      const left =
        axis !== 'vertical'
          ? result
          : clampToEdge === 'right'
            ? Math.max(0, dimensions.width - elementSize.width)
            : 0

      const width = elementSize.width
      const height = elementSize.height

      onNotchPositionChanged({ top, left, width, height })
    }

    // Return null for default positioning if no stored position
    return relativePosition == null ? null : result
  }, [
    relativePosition,
    dimensions,
    clampToEdge,
    elementSize,
    axis,
    isDraggingDisabled,
    isShadowDisabled,
    onNotchPositionChanged,
  ])

  const updateRelativePosition = useCallback(
    (absolute: number) => {
      const base = axis === 'vertical' ? dimensions.height : dimensions.width
      const relative = absolute / base
      setRelativePosition(relative)
      if (storageKey) {
        localStorage.setItem(storageKey, relative.toFixed(4))
      }
    },
    [axis, dimensions, storageKey],
  )

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isDraggingDisabled) return

      const base = axis === 'vertical' ? dimensions.height : dimensions.width
      const currentPos = axis === 'vertical' ? e.screenY : e.screenX
      const elSize = axis === 'vertical' ? elementSize.height : elementSize.width
      const startAbsolute =
        absolutePosition != null ? absolutePosition : Math.round(base / 2 - elSize / 2)

      dragStartRef.current = {
        startPos: currentPos,
        initial: startAbsolute,
      }

      const handleMouseMove = (e: MouseEvent) => {
        if (!dragStartRef.current || animationFrameId.current) return

        animationFrameId.current = requestAnimationFrame(() => {
          animationFrameId.current = null
          const dragStart = dragStartRef.current
          if (!dragStart) return

          // track either the vertical or horizontal position
          const currentPos = axis === 'vertical' ? e.screenY : e.screenX
          const delta = currentPos - dragStart.startPos
          let next = dragStart.initial + delta

          const max = axis === 'vertical' ? dimensions.height : dimensions.width
          if (clampToEdge) {
            next = Math.max(0, Math.min(next, max - elSize))
          }

          // mouse has moved, suppress click during drag
          if (Math.abs(delta) > 2) {
            isDraggingRef.current = true
            setIsDragging(true)
          }

          updateRelativePosition(next)
        })
      }

      const handleMouseUp = () => {
        dragStartRef.current = null
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current)
          animationFrameId.current = null
        }
        if (isDraggingRef.current) {
          // clear isDragging with slight delay to avoid click firing
          setTimeout(() => {
            isDraggingRef.current = false
            setIsDragging(false)
          }, 50)
        }
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [
      axis,
      clampToEdge,
      elementSize,
      absolutePosition,
      updateRelativePosition,
      dimensions,
      isDraggingDisabled,
    ],
  )

  return {
    style:
      absolutePosition != null
        ? axis === 'vertical'
          ? { top: absolutePosition }
          : { left: absolutePosition }
        : axis === 'vertical'
          ? { top: '50%' }
          : { left: '50%' },
    onMouseDown,
    isDragging,
  }
}
